/**
 * «Мои задачи» — сводный список по всем проектам, куда человек допущен.
 *
 * Сервис отдаёт задачи одной ручкой, но статус у задачи — это колонка её
 * проекта, а колонки лежат отдельно. Поэтому список догружает колонки тех
 * проектов, которые в нём действительно встретились: спрашивать про все
 * проекты подряд значит платить за то, чего на экране нет.
 */
import { issueApi } from '@/api'
import { toRow, type IssueRow } from '@/features/issues/api'
import { listColumns, type ProjectColumn } from '@/features/projects/api'

/** Отбор экрана — он же три чипа макета */
export type MyFilter = 'assigned' | 'watching' | 'overdue'

/** Задача сводного списка: строка плюс проект, по которому её группируют */
export interface MyIssue extends IssueRow {
  projectId: string
  /** Префикс ключа — он же ключ проекта: `SALES-214` → `SALES` */
  projectKey: string
}

const withProject = (row: IssueRow, projectId: string): MyIssue => ({
  ...row,
  projectId,
  projectKey: row.key.split('-')[0] ?? '',
})

/** Сколько задач тянем: это личный список, а не выгрузка проекта. */
const PAGE = 100

export const listMine = async (includeWatched: boolean): Promise<MyIssue[]> => {
  const { data } = await issueApi.issueServiceListMyIssues(includeWatched, true, '1', String(PAGE))

  return (data.issues ?? []).map((issue) => withProject(toRow(issue), issue.projectId ?? ''))
}

/** Просрочено — срок раньше сегодняшнего дня; сегодняшний срок ещё не долг. */
export const isOverdue = (issue: MyIssue, now = new Date()): boolean => {
  if (!issue.dueDate) return false

  const due = new Date(issue.dueDate)

  if (Number.isNaN(due.getTime())) return false

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()

  return due.getTime() < startOfToday
}

/** Группа списка: проект и его задачи */
export interface MyGroup {
  projectKey: string
  issues: MyIssue[]
}

/**
 * Группировка по проекту. Порядок групп задаёт порядок проектов в сайдбаре —
 * человек уже привык к нему, и второй порядок на соседнем экране только сбивал
 * бы.
 */
export const groupByProject = (issues: MyIssue[], order: string[]): MyGroup[] => {
  const groups = new Map<string, MyIssue[]>()

  for (const issue of issues) {
    groups.set(issue.projectKey, [...(groups.get(issue.projectKey) ?? []), issue])
  }

  const rank = (key: string) => {
    const at = order.indexOf(key)

    return at === -1 ? order.length : at
  }

  return [...groups.entries()]
    .map(([projectKey, rows]) => ({ projectKey, issues: rows }))
    .sort((a, b) => rank(a.projectKey) - rank(b.projectKey))
}

/** Колонки проектов, встретившихся в списке — по ним рисуются пилюли статусов. */
export const loadColumns = async (
  projectIds: string[],
): Promise<Record<string, ProjectColumn[]>> => {
  const unique = [...new Set(projectIds.filter(Boolean))]
  const loaded = await Promise.all(
    unique.map(async (id) => [id, await listColumns(id)] as const),
  )

  return Object.fromEntries(loaded)
}
